import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Circle,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

// 地図（OpenStreetMap + Leaflet・ADR-0006 / 基本設計 §2.6）。
// Leaflet 既定のマーカー画像はバンドラでパスが壊れるため、明示的に読み込んで設定する。
const markerHtmlIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// 苫小牧市中心付近（ピン未指定時の地図の初期中心）。
const TOMAKOMAI: [number, number] = [42.6341, 141.6055];

// 地図タイルの提供元（ADR-0006・§2.6）。
// - **現状（テスト公開のみ）**: OSM 公式タイル（既定）。
// - **本番昇格時**: 自前ホストへ切替（ビルド時に `VITE_TILE_URL=/tiles/{z}/{x}/{y}.png`）。
//   同一オリジン化で第三者への閲覧者IP送信を無くす。
const TILE_URL =
  import.meta.env.VITE_TILE_URL ?? "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
// 地図データの帰属（OSM データ由来のため自前ホストでも表記は必須・ODbL）。
const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

function round6(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}

export interface Coords {
  latitude: number | null;
  longitude: number | null;
}

// クリックでピンを置く（F-16）。地図クリックを拾って onChange に渡す。
function ClickToPlace({ onPick }: { onPick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onPick(round6(e.latlng.lat), round6(e.latlng.lng));
    },
  });
  return null;
}

// 既存座標の読込（編集時）で地図を追従させる（F-16）。
// center は初回マウント時のみ有効なため、reset() 後に座標が入っても中心が動かない。
// ただしユーザーのクリックは既に画面内なので中心を動かさない（skipRef で判別）。
function Recenter({
  target,
  skipRef,
}: {
  target: [number, number] | null;
  skipRef: React.MutableRefObject<boolean>;
}) {
  const map = useMap();
  const lat = target?.[0];
  const lng = target?.[1];
  useEffect(() => {
    if (lat == null || lng == null) return;
    if (skipRef.current) {
      skipRef.current = false;
      return;
    }
    map.setView([lat, lng], map.getZoom());
  }, [lat, lng, map, skipRef]);
  return null;
}

// 登録・編集フォーム用の地図ピッカー（F-16）。value が現在のピン、onChange で更新。
export function LocationPicker({
  value,
  onChange,
}: {
  value: Coords;
  onChange: (v: { latitude: number; longitude: number } | null) => void;
}) {
  const hasPin = value.latitude != null && value.longitude != null;
  const center: [number, number] = hasPin
    ? [value.latitude as number, value.longitude as number]
    : TOMAKOMAI;
  // クリック由来の座標変更では中心を動かさない（クリック位置は既に画面内）。
  const skipRecenter = useRef(false);

  return (
    <div className="space-y-1">
      <div className="overflow-hidden rounded-soft border border-slate-200">
        <MapContainer center={center} zoom={13} style={{ height: 280, width: "100%" }}>
          <TileLayer attribution={OSM_ATTRIBUTION} url={TILE_URL} />
          <Recenter target={hasPin ? center : null} skipRef={skipRecenter} />
          <ClickToPlace
            onPick={(lat, lng) => {
              skipRecenter.current = true;
              onChange({ latitude: lat, longitude: lng });
            }}
          />
          {hasPin && (
            <Marker
              position={[value.latitude as number, value.longitude as number]}
              icon={markerHtmlIcon}
            />
          )}
        </MapContainer>
      </div>
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>
          {hasPin
            ? `所在地: ${(value.latitude as number).toFixed(5)}, ${(value.longitude as number).toFixed(5)}`
            : "地図をクリックして所在地を指定できます（任意）"}
        </span>
        {hasPin && (
          <button
            type="button"
            className="font-bold text-brand hover:underline"
            onClick={() => onChange(null)}
          >
            ピンを消す
          </button>
        )}
      </div>
      <p className="text-[11px] text-slate-400">
        ※
        自宅を事業所にしている場合は、目印になる近くの地点でも構いません。ピンはいつでも変更・削除できます。
      </p>
    </div>
  );
}

// 詳細ページ用の読み取り専用マップ（F-17）。座標があるときだけ描画する。
export function CompanyLocationMap({
  latitude,
  longitude,
}: {
  latitude: number;
  longitude: number;
}) {
  return (
    <div className="space-y-1">
      <div className="overflow-hidden rounded-soft border border-slate-200">
        <MapContainer
          center={[latitude, longitude]}
          zoom={15}
          scrollWheelZoom={false}
          style={{ height: 240, width: "100%" }}
        >
          <TileLayer attribution={OSM_ATTRIBUTION} url={TILE_URL} />
          <Marker position={[latitude, longitude]} icon={markerHtmlIcon} />
        </MapContainer>
      </div>
    </div>
  );
}

export interface MapPin {
  id: string;
  name: string;
  area: string;
  industryLabel: string;
  latitude: number;
  longitude: number;
}

// 全体地図（F-18）。座標ありの公開企業をピン表示。ピン→詳細へのリンク。
export function AllCompaniesMap({ pins }: { pins: MapPin[] }) {
  return (
    <div className="space-y-1">
      <div className="overflow-hidden rounded-soft border border-slate-200">
        <MapContainer center={TOMAKOMAI} zoom={12} style={{ height: 460, width: "100%" }}>
          <TileLayer attribution={OSM_ATTRIBUTION} url={TILE_URL} />
          {pins.map((p) => (
            <Marker key={p.id} position={[p.latitude, p.longitude]} icon={markerHtmlIcon}>
              <Popup>
                <div className="space-y-1">
                  <p className="font-bold text-brand-dark">{p.name}</p>
                  <p className="text-xs text-slate-500">
                    {p.area}・{p.industryLabel}
                  </p>
                  <Link
                    to={`/companies/${p.id}`}
                    className="text-xs font-bold text-brand underline"
                  >
                    詳細を見る
                  </Link>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}

// 半径検索の地図（F-19）。クリックで中心を置き、半径の円を描く。
export function RadiusSearchMap({
  center,
  radiusKm,
  onPickCenter,
}: {
  center: Coords;
  radiusKm: number;
  onPickCenter: (lat: number, lng: number) => void;
}) {
  const hasCenter = center.latitude != null && center.longitude != null;
  const pos: [number, number] = hasCenter
    ? [center.latitude as number, center.longitude as number]
    : TOMAKOMAI;
  return (
    <div className="space-y-1">
      <div className="overflow-hidden rounded-soft border border-slate-200">
        <MapContainer center={TOMAKOMAI} zoom={12} style={{ height: 420, width: "100%" }}>
          <TileLayer attribution={OSM_ATTRIBUTION} url={TILE_URL} />
          <ClickToPlace onPick={(lat, lng) => onPickCenter(lat, lng)} />
          {hasCenter && (
            <>
              <Marker position={pos} icon={markerHtmlIcon} />
              <Circle center={pos} radius={radiusKm * 1000} pathOptions={{ color: "#2563eb" }} />
            </>
          )}
        </MapContainer>
      </div>
      <p className="text-[11px] text-slate-400">地図をクリックして中心を指定してください。</p>
    </div>
  );
}
